import React, { useRef } from 'react';
import styled from 'styled-components';
import { Form } from 'react-bootstrap';

import type { IFormInput } from './types';

import { parseInputType, classList } from '@/helpers';
import { sizing } from '@/styles/theme';
import { ShowPass } from './ShowPass';
import { Button } from '../Buttons';

const InputAlternative = React.forwardRef((props: IFormInput, ref): React.ReactElement => {
  const {
    className,
    children,
    id,
    label,
    required,
    register,
    error,
    touched = false,
    onButtonClick,
    buttonLabel,
    type,
    readOnly = false,
    ...rest
  } = props;

  const { ...registerData } = register ? register(rest.name) : {};
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isButton = onButtonClick && buttonLabel;

  return (
    <Fieldset className={`${className}`}>
      {children}
      <InputLabelAlternative htmlFor={id || rest.name}>
        {required && <span className="text-danger me-1">*</span>}
        {label}
      </InputLabelAlternative>
      <div className={`position-relative mt-1 ${isButton ? 'd-flex' : ''}`} style={{ maxWidth: 'auto' }}>
        <div>
          <AreaCode>+{rest.countrycode}</AreaCode>
          <div>
            <StyledInput
              id={id || rest.name}
              className={classList(error && touched && 'error', readOnly && 'read-only')}
              ref={(element: HTMLInputElement) => {
                ref && ref instanceof Function && ref(element);
                inputRef.current = element;
              }}
              paddingleft="4rem !important"
              {...parseInputType(type)}
              {...registerData}
              {...rest}
            />
          </div>
        </div>
        {rest.showpass && <ShowPass inputElementName={rest.name} />}
        {isButton && (
          <GroupedButton onClick={onButtonClick} size="m">
            {buttonLabel}
          </GroupedButton>
        )}
      </div>
      <ErrorMessage className={readOnly ? 'read-only' : ''}>
        {(error && touched) || error?.type === 'api' ? error.message : ''}
      </ErrorMessage>
    </Fieldset>
  );
});
InputAlternative.displayName = 'InputAlternative';

export { InputAlternative, InputLabelAlternative };

const Fieldset = styled.fieldset`
  font-size: ${sizing.fontSize.sm};
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const InputLabelAlternative = styled.label.attrs({
  className: 'mb-1',
})`
  font-size: 16px;
  line-height: 24px;
  color: #374151;
  display: flex;
  align-items: center;
`;

const StyledInput = styled(Form.Control).attrs({
  className: 'w-100 px-3 border rounded-3',
})`
  background-color: #f9fafb;
  height: 46px;
  padding-left: ${props => props.paddingleft || '1rem !important'};

  &.error {
    border: 1px solid #dc3545 !important;
  }

  &:disabled {
    color: #868e96;
    border: none !important;
    background-color: #fff;
    padding-left: 0 !important;
    font-size: 20px;
    line-height: 30px;
  }
`;

const ErrorMessage = styled.div`
  padding-top: 4px;
  font-size: 12px;
  line-height: 19px;
  height: 22px;
  color: #dc3545;

  &.read-only {
    display: none;
  }
`;

const GroupedButton = styled(Button)`
  background-color: #fff;
  height: 42px;
  margin-left: 10px;
`;

const AreaCode = styled.div`
  padding: 11px;
  height: 100%;
  min-width: 40px;
  font-size: 16px;
  color: #bfbdbd;
  position: absolute;
  background-color: #f5f5f5;
  border: 1px solid #dadee1;
  border-top-left-radius: 0.3rem;
  border-bottom-left-radius: 0.3rem;
`;
